import { format } from 'date-fns';
import { formatDatetimeWithSeconds } from 'src/utils/dateUtils';
import type { Span } from './types';

/**
 * The wall clock a trace's rows are read against: `Span.startMs` is an offset from the run's
 * start, so a row's real time is the two added together.
 */
export interface RunClock {
  runStartMs: number;
  /** The run crosses local midnight, so a bare `HH:mm:ss` would be ambiguous. */
  withDate: boolean;
}

function latestEndMs(spans: Span[]): number {
  let latest = 0;
  for (const span of spans) {
    latest = Math.max(
      latest,
      span.startMs + (span.durMs ?? 0),
      span.children ? latestEndMs(span.children) : 0,
    );
  }
  return latest;
}

/** The clock for a run's rows, or null when the run's start is unknown. */
export function runClock(
  runStartedAt: string | null | undefined,
  spans: Span[],
): RunClock | null {
  const runStartMs = runStartedAt ? Date.parse(runStartedAt) : Number.NaN;
  if (!Number.isFinite(runStartMs)) return null;
  const endMs = runStartMs + latestEndMs(spans);
  return {
    runStartMs,
    withDate:
      new Date(runStartMs).toDateString() !== new Date(endMs).toDateString(),
  };
}

/** A row's start as the viewer's local wall-clock time, plus the full timestamp for a tooltip. */
export function spanWallTime(
  clock: RunClock,
  span: Pick<Span, 'startMs'>,
): { label: string; full: string } {
  const at = new Date(clock.runStartMs + span.startMs);
  return {
    label: format(at, clock.withDate ? 'MMM d HH:mm:ss' : 'HH:mm:ss'),
    full: formatDatetimeWithSeconds(at),
  };
}
